import { useMemo, useState } from "react";
import { Box, Typography, FormControl, MenuItem, Select, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../ui/colors";

export const WidgetComponentCharts = ({
  swapyName,
  sxProp,
  icon,
  title,
  description,
  data,
  dataLabel,
  dataSize,
  sideData,
  sideDataLabel,
  sideDataSize,
  children,
  refResizeObserver,
  variant = "default",
  actions,
  bottomLeft,
  bottomRight,

  ...props
}) => {
  const isXs = useMediaQuery("(max-width:600px)");
  const hasSideData = Boolean(sideData);

  const [mobileView, setMobileView] = useState("main"); // "main" | "side"

  const mobileOptions = useMemo(() => {
    const opts = [{ value: "main", label: dataLabel }];
    if (hasSideData) opts.push({ value: "side", label: sideDataLabel });
    return opts;
  }, [hasSideData, dataLabel, sideDataLabel]);

  return (
    <Box sx={{ height: "100%", width: "100%", minWidth: 0 }} data-swapy-slot={swapyName}>
      <Box
        data-swapy-item={swapyName}
        ref={refResizeObserver}
        component={motion.div}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        {...props}
        sx={{
          cursor: "grab",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          borderRadius: "24px",
          padding: 3,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          position: "relative",
          border: `1px solid ${colors.gray[50]}`,
          "&:hover": {
            border: `1px solid ${colors.orange[100]}`,
            "&:hover [data-widget-title]": { color: colors.gray[600] },
            "&:hover [data-widget-description]": { opacity: 1 },
          },
        }}
        {...sxProp}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "auto",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box sx={{ height: 53, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography
                data-widget-title
                variant="h6"
                sx={{
                  fontWeight: 500,
                  lineHeight: "normal",
                  justifyContent: "center",
                  fontSize: "1.4rem",
                  color: colors.gray[400],
                  transition: "font-weight 0.15s ease, color 0.15s ease",
                }}
              >
                {title}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderRadius: 8, backgroundColor: "var(--fx-surface-base, #FFFFFF)", flexShrink: 0 }}>{actions}</Box>
        </Box>

        {/* Description */}
        {description && (
          <>
            <Box
              sx={{
                height: 35,
                overflow: "hidden",
              }}
            >
              <Typography
                data-widget-description
                variant="body2"
                sx={{
                  color: colors.gray[400],
                  fontSize: "0.8rem",
                  transition: "all 0.15s ease",
                  opacity: 0.35,
                  flexShrink: 0,
                }}
              >
                {description}
              </Typography>
            </Box>
          </>
        )}

        <Box
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Desktop / tablet */}
          {!isXs && (
            <Box
              sx={{
                display: "flex",
                flex: 1,
                minHeight: 0,
                width: "100%",
                minWidth: 0,
                gap: 2,
                alignItems: "stretch",
                // border: "1px solid pink"
              }}
            >
              {hasSideData && (
                <Box
                  data-swapy-ignore
                  sx={{
                    flex: sideDataSize,
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    minHeight: 0,
                    border: "1px solid cyan",
                  }}
                >
                  {sideData}
                </Box>
              )}

              <Box
                sx={{
                  flex: hasSideData ? dataSize : "1 1 auto",
                  minWidth: 0,
                  display: "flex",
                  justifyContent: hasSideData ? "flex-end" : "center",
                  alignItems: "center",
                  height: "100%",
                  minHeight: 0,
                  //border: "1px solid cyan"
                }}
              >
                {data}
              </Box>
            </Box>
          )}

          {/* Mobile (xs) */}
          {isXs && (
            <Box
              sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {hasSideData && mobileView === "side" ? (
                <Box data-swapy-ignore sx={{ width: "100%", height: "100%", minHeight: 0, minWidth: 0 }}>
                  {sideData}
                </Box>
              ) : (
                <Box sx={{ width: "100%", height: "100%", minHeight: 0, minWidth: 0 }}>{data}</Box>
              )}

              {hasSideData && (
                <Box
                  data-swapy-ignore
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  <FormControl size="small" variant="outlined">
                    <Select value={mobileView} onChange={(e) => setMobileView(e.target.value)} sx={{ minWidth: 150, height: 32, fontSize: 12 }}>
                      {mobileOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: "auto",
            pt: 2,
            flexShrink: 0,
            minWidth: "10px",
            width: "20px",
            maxWidth: "20px",
          }}
        >
          <Box>{bottomLeft}</Box>
          <Box>{bottomRight}</Box>
        </Box>
      </Box>
    </Box>
  );
};
